/**
 * buildLedgerRows (#1315) — merge bank facts (history) and planned
 * transactions (future) into one chronological ledger. Each row carries the
 * running balance AFTER that transaction (same-day rows accumulate to the
 * day's end-of-day figure from the computed series) and an actual/projected
 * flag at the anchor boundary.
 */
import { categoryLabel, isLedgerCategory, type LedgerCategory } from './ledgerCategory';
import { wjStartYear } from './monthLensItems';
import {
  commitmentKind,
  headRecord,
  recordToPlanned,
  signedTotal,
  transactionHeadlineSigned,
  transactionState,
  transactionVariance,
} from './transactions';
import type {
  BalanceSeriesResult,
  BankTransaction,
  PlannedTransaction,
  ReconInfo,
  Transaction,
} from './types';

export interface LedgerRow {
  id: string;
  date: string;
  label: string;
  sublabel: string;
  amountEur: number; // signed: income +, expense −
  balanceEur: number; // running balance AFTER this transaction
  isActual: boolean;
  /** Source planned tx — present only on editable plan rows, not bank facts. */
  planned?: PlannedTransaction;
  /** Plan↔Ist reconciliation state (#1414) — present once reconciliation runs. */
  recon?: ReconInfo;
  /** Grouped lifecycle transaction (transaction-records) — drives the
   *  geplant→kontrahiert→bezahlt chips + qty×price breakdown in LedgerFeed. */
  transaction?: Transaction;
  /** A planned row whose date is in the past (≤ today) with no bank
   *  confirmation: it "did not happen". Rendered greyed, mirroring the chart
   *  (which flat-carries across the unconfirmed gap). VISUAL ONLY as of #3065 —
   *  see ``zeroBalanceEffect`` for the balance/aggregate-math gate, which the two
   *  used to share until a settled collective-credit expectation needed them to
   *  diverge (un-greyed, but still zero its OWN balance effect — the real cash
   *  movement is already counted via its ``payed`` sibling elsewhere). */
  didNotHappen?: boolean;
  /** Whether this row's ``amountEur`` contributes to the running balance
   *  (``withRunningBalance``) and to monthly/Säulen aggregates
   *  (``monthlyColumns.ts``) — ``true`` means EXCLUDED, mirroring the chart's
   *  flat-carry. Was folded into ``didNotHappen`` pre-#3065; split out because a
   *  settled `contracted`-only collective-credit expectation (#3065) must render
   *  un-greyed (``didNotHappen: false``) while STILL excluding its own amount —
   *  the credit's ``payed`` sibling already counts that money, on ITS OWN
   *  (later) settlement date, so un-excluding the invoice's earlier-dated row
   *  too would double it into the displayed balance/Säulen for that day/month. */
  zeroBalanceEffect?: boolean;
  /** The raw L2 category id (#2743) — the STORED value, not the German label
   *  ``sublabel`` carries (which is blanked for ``unsorted`` and would collapse
   *  "no category" into "explicitly unsorted"). Aggregators that need to treat a
   *  category differently — the Säulen presentation muting ``transfer_internal``
   *  own-account movement — read this. ``undefined`` where the source fact has
   *  none (a legacy ``BankTransaction`` predating #2161's categorisation) or
   *  carries a value that is not a declared id. */
  ledgerCategory?: LedgerCategory;
}

export function buildLedgerRows(
  bankFacts: BankTransaction[],
  planned: PlannedTransaction[],
  series: BalanceSeriesResult,
  horizonDate: string,
): LedgerRow[] {
  const balanceByDate = new Map<string, number>();
  for (const p of series.dailySeries) balanceByDate.set(p.date, p.balanceEur);

  const rows: LedgerRow[] = [];

  bankFacts.forEach((b, i) => {
    rows.push({
      id: `bank-${b.statement_id}-${b.booking_date}-${i}`,
      date: b.booking_date,
      label: b.counterparty || b.purpose || 'Buchung',
      sublabel: b.purpose,
      amountEur: b.amount_eur,
      balanceEur: balanceByDate.get(b.booking_date) ?? 0,
      isActual: b.booking_date <= horizonDate,
      // Optional AND untyped on the wire (``types.ts:76``) — guard it rather
      // than casting, so a legacy or malformed value lands as ``undefined``
      // instead of as an id the aggregators would then act on.
      ledgerCategory: isLedgerCategory(b.ledger_category) ? b.ledger_category : undefined,
    });
  });

  planned.forEach((p, i) => {
    const signed = p.direction === 'income' ? p.amount_eur : -p.amount_eur;
    rows.push({
      id: p.derived_fact_id ?? `plan-${p.value_date}-${i}`,
      date: p.value_date,
      label: p.label || categoryLabel(p.ledger_category),
      // Show the categorisation (German GuV category name); hide the noisy
      // "Unsortiert" chip for not-yet-categorised rows.
      sublabel: p.ledger_category === 'unsorted' ? '' : categoryLabel(p.ledger_category),
      amountEur: signed,
      balanceEur: balanceByDate.get(p.value_date) ?? 0,
      isActual: p.value_date <= horizonDate,
      planned: p,
      ledgerCategory: p.ledger_category,
    });
  });

  return withRunningBalance(
    rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
    balanceByDate,
  );
}

/**
 * Build the ledger from grouped transactions (transaction-records task) — one
 * row per transaction, dated at its payed record (else planned/contracted, so
 * an unpaid row tracks the planned date/amount the series projected — #1495),
 * carrying the headline amount, the derived recon state + Δ (for ReconBadge),
 * and the grouped ``transaction`` (lifecycle chips + qty×price breakdown).
 * Replaces #1414's reconcileLedger: the match is the authored ``transaction_id``,
 * not a runtime heuristic. ``withRunningBalance`` still spreads same-day rows.
 */
export function buildTransactionLedgerRows(
  transactions: Transaction[],
  series: BalanceSeriesResult,
  horizonDate: string,
): LedgerRow[] {
  const balanceByDate = new Map<string, number>();
  for (const p of series.dailySeries) balanceByDate.set(p.date, p.balanceEur);

  // #3241: the boundary the did-not-happen/zero-balance JUDGEMENT uses — never the
  // rendered one. ``horizonDate`` is the widened aggregate label under „Alle
  // Konten", but "the bank confirmed through here, so this plan did not happen"
  // only holds where EVERY account is confirmed, i.e. at ``verdictHorizon``.
  // Judging on the widened label greys a live plan as "did not happen" and zeroes
  // it out of the running balance + Säulen while ``computeBalanceSeries`` still
  // counts it in the curve (it sits past the DORMANT account's own horizon), so
  // row and chart disagree in the direction that OVER-states available cash —
  // the exact failure the #3107 note below exists to prevent. Taking the earlier
  // of the two is inert for a per-account series, where they are equal by
  // construction, and for the neutral ('') series.
  const judgedHorizon = series.verdictHorizon < horizonDate ? series.verdictHorizon : horizonDate;

  const rows: LedgerRow[] = transactions.map((tx) => {
    const date = headRecord(tx).value_date;
    const variance = transactionVariance(tx);
    const state = transactionState(tx);
    const recon: ReconInfo = {
      state,
      istAmountEur: tx.payed ? signedTotal(tx.payed) : undefined,
      // Against the plan when there is one, else against the CONTRACT (#3026) —
      // the same precedence ``transactionState`` judges by, so the ⚠ chip's Δ
      // always names the figure the verdict was reached against instead of
      // rendering bare on a plan-less settled invoice.
      deltaEur: variance.payedVsPlannedEur ?? variance.payedVsContractedEur ?? undefined,
      // …and WHICH figure that was, so the chip's German copy can say so (#3026).
      commitment: commitmentKind(tx),
      plannedId: tx.planned?.derived_fact_id,
      bankId: tx.payed?.derived_fact_id,
    };
    // A planned-only row INSIDE the confirmed-actual window (date ≤ the derived
    // Ist horizon — #1639) that the bank never confirmed did not happen — grey it
    // and zero its balance effect (the chart excludes it too). The horizon advances
    // with the latest payed booking, so the current, just-reconciled days grey in
    // lockstep; anything after the horizon is normal projection, never greyed.
    const withinUnconfirmedWindow = state === 'geplant' && !!judgedHorizon && date <= judgedHorizon;
    // #3065: the GREY is suppressed for a contracted-only expectation whose
    // settled_at IS stamped. A collective 1:N buyer credit settles every matched
    // expectation but the matcher only links the FIRST into a payed sibling (the
    // #3026-era guard), so expectations 2..N stay contracted-only forever —
    // greying them as "did not happen" would be wrong: their money already
    // arrived, it just has no payed record of its OWN to prove it.
    // transactionState stays 'geplant' (#1495's curve-agreement invariant is
    // untouched, the fix lives here only).
    const didNotHappen = withinUnconfirmedWindow && !tx.contracted?.settled_at;
    // The BALANCE/aggregate math deliberately does NOT follow the grey suppression
    // above (codex review, #3065): a settled expectation's own row must still
    // contribute ZERO to the running balance / monthly Säulen. The real cash
    // movement is already fully counted via the credit's `payed` sibling — a
    // DIFFERENT transaction/row, dated at the (later) settlement, not the
    // (earlier) invoice date. Un-excluding this row too would double that money
    // into the displayed balance/Säulen for the invoice's own day/month, even
    // though `computeBalanceSeries` itself never double-counts (it reads
    // `planned`/`payed` only, never `contracted`) — this guards the DISPLAY math
    // that walks `amountEur` per row, not the engine.
    //
    // #3107 generalises that split from "settled, inside the window" to EVERY
    // contracted-only row, at any date. `withinUnconfirmedWindow` is
    // `state === 'geplant' && date <= horizonDate`, so a FUTURE-dated
    // contracted-only row used to fall through with `false` — and then fed both
    // `withRunningBalance` here and the Säulen in `monthlyColumns.ts`, while
    // `computeBalanceSeries` never counted it. Row and chart disagreed by
    // construction, in the direction that OVER-STATES available cash. That was
    // latent before the #3107 bridge (only a fact-substrate tenant could hit it)
    // and would have shipped at ~4.772-row scale on WF the moment contracted
    // records reached the ledger.
    //
    // This is a strict generalisation of the #3065 rule, not a new one: the
    // curve is planned/payed only (#1495), so a contracted-only row NEVER has a
    // counterpart in the series and must never move the display math derived
    // from it — before or after the horizon. Contracted+payed and
    // planned+contracted rows are untouched: they carry a record the curve does
    // count, so they keep their balance effect.
    const contractedOnly = !!tx.contracted && !tx.payed && !tx.planned;
    const zeroBalanceEffect = withinUnconfirmedWindow || contractedOnly;
    return {
      id: tx.id,
      date,
      label: tx.label || categoryLabel(tx.ledger_category),
      sublabel: tx.ledger_category === 'unsorted' ? '' : categoryLabel(tx.ledger_category),
      amountEur: transactionHeadlineSigned(tx),
      balanceEur: balanceByDate.get(date) ?? 0,
      isActual: date <= horizonDate,
      // The edit pencil expects a PlannedTransaction; provide one from the
      // planned record so the existing edit/categorize flow is unchanged.
      planned: tx.planned ? recordToPlanned(tx.planned) : undefined,
      transaction: tx,
      recon,
      didNotHappen,
      zeroBalanceEffect,
      // Required and typed here (``types.ts:233``), unlike the bank-fact lane.
      ledgerCategory: tx.ledger_category,
    };
  });

  return withRunningBalance(
    rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
    balanceByDate,
  );
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Assign a per-transaction running balance. The daily series gives each day's
 * end-of-day balance (= the LAST transaction of that day); earlier same-day
 * rows are that EOD minus the amounts of the rows after them. ``rows`` must be
 * date-sorted (same-day rows contiguous). Mutates and returns ``rows``.
 *
 * Without this, every transaction on a shared day shows the identical EOD
 * figure instead of its own intra-day running balance.
 */
export function withRunningBalance(
  rows: LedgerRow[],
  eodByDate: Map<string, number>,
): LedgerRow[] {
  for (let i = 0; i < rows.length; ) {
    let j = i;
    while (j < rows.length && rows[j].date === rows[i].date) j++;
    let bal = eodByDate.get(rows[i].date) ?? 0;
    for (let k = j - 1; k >= i; k--) {
      rows[k].balanceEur = round2(bal);
      // A zero-balance-effect row never moved the account, so it does not shift
      // the running balance of the rows before it (its own figure equals the
      // balance after it — i.e. unchanged). Deliberately ``zeroBalanceEffect``,
      // NOT ``didNotHappen`` (#3065 codex review): the two diverge for a settled
      // contracted-only collective-credit expectation, which renders un-greyed
      // (``didNotHappen: false``) but must still contribute nothing here — its
      // money is already counted via the credit's own ``payed`` row.
      bal = round2(bal - (rows[k].zeroBalanceEffect ? 0 : rows[k].amountEur));
    }
    i = j;
  }
  return rows;
}

/** The ledger rows whose ``date`` falls in the ``YYYY-MM`` month, preserving
 *  chronological order. An empty ``ym`` means "no filter" → the rows unchanged
 *  (same reference), so the page falls back to the full record. */
export function rowsInMonth(rows: LedgerRow[], ym: string): LedgerRow[] {
  if (!ym) return rows;
  return rows.filter((r) => r.date.slice(0, 7) === ym);
}

/** The ledger rows whose month falls in the Wirtschaftsjahr starting in
 *  ``startYear`` (#2254), preserving chronological order. The year is an
 *  explicit number — a no-activity WJ yields ``[]``, deliberately unlike
 *  ``rowsInMonth('')``'s "empty means all". */
export function rowsInWj(rows: LedgerRow[], startYear: number, wjStartMonth: number): LedgerRow[] {
  return rows.filter((r) => wjStartYear(r.date.slice(0, 7), wjStartMonth) === startYear);
}
